import { ChangeDetectionStrategy, Component, inject, linkedSignal, signal } from '@angular/core';
import { FormField, form, required } from '@angular/forms/signals';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { finalize } from 'rxjs';

import { Product, ProductStatuses } from '../schemas';
import { ClientService, Messenger, ProductService, UserService } from '../service';
import { Autocomplete } from './autocomplete';

export interface ProductFormData {
  title?: string;
  product?: Product;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    FormField,
    MatTooltipModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatProgressBarModule,
    Autocomplete,
  ],
  templateUrl: './product-form.html',
  styles: ``,
})
export class ProductForm {
  pending = signal(false);

  userService = inject(UserService);
  clientService = inject(ClientService);

  productOwners = this.userService.names;

  clientCodes = this.clientService.codes;

  statusOptions = signal(ProductStatuses);

  productService = inject(ProductService);
  messenger = inject(Messenger);

  data = (inject(MAT_DIALOG_DATA) as ProductFormData) || {};
  hostDialog = inject(MatDialogRef);

  model = linkedSignal(
    () =>
      ({
        ClientCode: '',
        JobNumber: '',
        JiraTitle: '',
        Status: '',
        Title: '',
        ProductOwner: '',
        Commissionable: false,
        Notes: '',
        BudgetNote: '',
        ...this.data.product,
      }) as Required<Product>,
  );

  form = form(this.model, (schema) => {
    required(schema.ClientCode);
    required(schema.Status);
    required(schema.Title);
  });

  save() {
    const data = this.form().value();

    this.pending.set(true);
    this.productService
      .save(data, !!this.data.product)
      .pipe(finalize(() => this.pending.set(false)))
      .subscribe({
        next: (client: Product) => {
          this.hostDialog.close(client);
        },
        error: this.messenger.handleHttpError,
      });
  }
}
